// CreateChildTicketModal — quick path to spawn a brand-new ticket linked under the
// current ticket as its child. For complex tickets agents should navigate to the full
// CreateTicket form (which also supports parent-context — see CreateTicketForm.js).
//
// V1 design choice — inherit category/priority from the parent.
//   Asking the agent to re-pick category/priority drop-downs in a small modal would
//   slow them down and risk data drift. We pass through the parent's IDs by default
//   (the agent can still edit the resulting child ticket after creation). The modal
//   shows a clear "inherited from parent" line so this isn't surprising.
//
// On success this modal calls onCreated(childTicketResponse, relationResponse) so the
// parent component can refresh the LinkedTicketsPanel and optionally route the agent
// to the new ticket.

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Plus, Loader2, AlertCircle } from 'lucide-react';
import { ticketService } from '../../api/ticketService';

const errorMessageFor = (err) => {
  const fromResponse = err?.response?.data?.message || err?.data?.message;
  if (fromResponse) return fromResponse;
  if (err?.message) return err.message;
  return 'Unable to create child ticket.';
};

const CreateChildTicketModal = ({
  parentTicket, // { ticketId, ticketNumber, categoryId, priorityId, ticketTypeId, departmentId, teamId, ... }
  isOpen,
  onClose,
  onCreated,
}) => {
  const navigate = useNavigate();
  const [subject, setSubject] = useState('');
  const [description, setDescription] = useState('');
  const [openAfterCreate, setOpenAfterCreate] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const reset = () => {
    setSubject('');
    setDescription('');
    setOpenAfterCreate(false);
    setError(null);
    setSubmitting(false);
  };

  const handleClose = () => {
    if (submitting) return;
    reset();
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const trimmedSubject = subject.trim();
    const trimmedDescription = description.trim();

    if (trimmedDescription.length < 10) {
      setError('Description must be at least 10 characters.');
      return;
    }
    if (trimmedSubject.length > 500) {
      setError('Subject must not exceed 500 characters.');
      return;
    }
    if (trimmedDescription.length > 5000) {
      setError('Description must not exceed 5000 characters.');
      return;
    }
    if (!parentTicket?.categoryId) {
      // We can't safely synthesize a category server-side; surface this clearly.
      setError(
        'Parent ticket has no category. Please use the full Create Ticket page instead.',
      );
      return;
    }

    const ticketRequest = {
      subject: trimmedSubject || null,
      description: trimmedDescription,
      categoryId: parentTicket.categoryId,
      priorityId: parentTicket.priorityId,
      ticketTypeId: parentTicket.ticketTypeId || null,
      departmentId: parentTicket.departmentId || null,
      teamId: parentTicket.teamId || null,
      source: 'portal',
      channel: 'web',
      // location fields and custom fields are intentionally omitted — agents who need
      // them should navigate to the full CreateTicket page, which also supports
      // parent-context (?parentTicketId=...).
    };

    setSubmitting(true);
    try {
      const res = await ticketService.createChildTicket(parentTicket.ticketId, ticketRequest);
      const apiData = res?.data;
      if (apiData?.success === false) {
        throw new Error(apiData?.message || 'Create child failed');
      }
      const payload = apiData?.data || apiData;
      const childResp = payload?.child;
      const relationResp = payload?.relation;
      reset();
      onCreated?.(childResp, relationResp);
      onClose();
      if (openAfterCreate && childResp?.ticketId) {
        navigate(`/agent/tickets/${childResp.ticketId}`);
      }
    } catch (err) {
      setError(errorMessageFor(err));
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-lg bg-white dark:bg-gray-800 shadow-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <Plus className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              Create child ticket
            </h3>
          </div>
          <button
            type="button"
            onClick={handleClose}
            disabled={submitting}
            className="rounded p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
          <div className="rounded border border-blue-200 dark:border-blue-800 bg-blue-50 dark:bg-blue-900/20 px-3 py-2 text-sm text-blue-800 dark:text-blue-200">
            New ticket will be created as a child of{' '}
            <span className="font-mono font-medium">
              {parentTicket?.ticketNumber || `#${parentTicket?.ticketId}`}
            </span>{' '}
            and inherit its category, priority, team, and requester. You can edit those on
            the child ticket after creation.
          </div>

          <div>
            <label htmlFor="cc-subject" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Subject <span className="text-gray-400 font-normal">(optional, max 500)</span>
            </label>
            <input
              id="cc-subject"
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              disabled={submitting}
              maxLength={500}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              autoFocus
            />
          </div>

          <div>
            <label htmlFor="cc-description" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Description <span className="text-red-500">*</span>{' '}
              <span className="text-gray-400 font-normal">(10–5000 chars)</span>
            </label>
            <textarea
              id="cc-description"
              rows={5}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              disabled={submitting}
              maxLength={5000}
              className="mt-1 block w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-900 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              required
            />
            <div className="mt-1 text-xs text-gray-400 dark:text-gray-500 text-right">
              {description.length}/5000
            </div>
          </div>

          <label className="flex items-center text-sm text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={openAfterCreate}
              onChange={(e) => setOpenAfterCreate(e.target.checked)}
              disabled={submitting}
              className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500 mr-2"
            />
            Open the new child ticket after creation
          </label>

          {error && (
            <div className="flex items-start gap-2 rounded border border-red-200 dark:border-red-700 bg-red-50 dark:bg-red-900/20 px-3 py-2 text-sm text-red-700 dark:text-red-300">
              <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
              <div className="break-words">{error}</div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={handleClose}
              disabled={submitting}
              className="px-4 py-2 text-sm font-medium rounded-md border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center px-4 py-2 text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
            >
              {submitting && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
              Create child
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateChildTicketModal;
